/**
 *  @swagger
 *  definitions:
 *    ChampionshipResultTableRaceParams:
 *      type: object
 *      properties:
 *        ChampionshipResultTableId:
 *          description: ID of the associated ChampionshipResultTable
 *          type: string
 *        RegattaId:
 *          description: ID of the associated ChampionshipResultTable
 *          type: string
 *        Name:
 *          description: Name of the ChampionshipResultTableRace
 *          type: string
 *        Slug:
 *          description: Slug of the ChampionshipResultTableRace
 *          type: string
 *        Undiscard:
 *          description: Undiscard of the ChampionshipResultTableRace
 *          type: boolean
 *        Dominant:
 *          description: Dominant of the ChampionshipResultTableRace
 *          type: boolean
 *        Coef:
 *          description: Coef of the RegattaResultTableRace
 *          type: integer
 *
 *  @swagger
 *  definitions:
 *    ChampionshipResultTableRaceCreateParams:
 *      allOf:
 *        - $ref: '#/definitions/ChampionshipResultTableRaceParams'
 *        - type: object
 *          required:
 *            - ChampionshipResultTableId
 *            - Name
 *            - Slug
 *            - Undiscard
 *            - Dominant
 *            - Coef
 *
 *  @swagger
 *  definitions:
 *    ChampionshipResultTableRaceQueryParams:
 *      allOf:
 *        - type: object
 *          properties:
 *            id:
 *              description: ID of the ChampionshipResultTableRace
 *              type: string
 *        - $ref: '#/definitions/ChampionshipResultTableRaceParams'
 *
 *  @swagger
 *  definitions:
 *    ChampionshipResultTableRaceUpdateParams:
 *      allOf:
 *        - $ref: '#/definitions/ChampionshipResultTableRaceQueryParams'
 *        - type: object
 *          required:
 *            - id
 *
 *  @swagger
 *  definitions:
 *    ChampionshipResultTableRace:
 *      allOf:
 *        - $ref: '#/definitions/ChampionshipResultTableRaceCreateParams'
 *        - $ref: '#/definitions/ChampionshipResultTableRaceUpdateParams'
 *        - $ref: '#/definitions/Timestamps'
 *
 *  @swagger
 *  components:
 *    schemas:
 *      ChampionshipResultTableRace:
 *        allOf:
 *          - $ref: '#/definitions/ChampionshipResultTableRace'
 *          - type: object
 *            properties:
 *              ChampionshipResultTable:
 *                description: ChampionshipResultTable of the ChampionshipResultTableRace
 *                $ref: '#/components/schemas/ChampionshipResultTable'
 *              Regatta:
 *                description: Regatta of the ChampionshipResultTableRace
 *                $ref: '#/components/schemas/Regatta'
 *              ChampionshipResultTableRaceResults:
 *                description: ChampionshipResultTableRaceResults of the ChampionshipResultTableRace
 *                type: array
 *                items:
 *                  $ref: '#/components/schemas/ChampionshipResultTableRaceResult'
 */

// Types
import type {
  $Model,
  $ModelClass,
  $ModelClasses,
} from '.';
import type {
  $Field,
} from '../types';

export type $RelationIds = {
  ChampionshipResultTableId: string;
  RegattaId: null | string;
};

export type $Params = {
  Coef: number;
  Dominant: boolean;
  Name: string;
  Slug: string;
  Undiscard: boolean;
};

export type $CreateParams = $Params & $RelationIds;

export type $UpdateParams = {
  id: string;
} & Partial<$CreateParams>;

export type $UpsertParams = $CreateParams & {
  id?: string;
};

export type $QueryParams = {
  id?: null | string;
} & Partial<$CreateParams>;

export type $Associations = {
  ChampionshipResultTable?: $Model<'ChampionshipResultTable'>;
  ChampionshipResultTableRaceResults?: Array<$Model<'ChampionshipResultTableRaceResult'>>;
  Regatta?: $Model<'Regatta'>;
};

export type $ModelParams = $Associations & $CreateParams & {
  id: string;
};

type $Fields = $Params & {
  id: string;
};

const fields = (
  Sequelize,
): Record<keyof $Fields, $Field> => ({
  Coef: {
    allowNull: false,
    type: Sequelize.DOUBLE(
      2,
      1,
    ),
  },
  Dominant: {
    allowNull: false,
    defaultValue: false,
    type: Sequelize.BOOLEAN,
  },
  id: {
    allowNull: false,
    defaultValue: Sequelize.UUIDV4,
    primaryKey: true,
    type: Sequelize.UUID,
    unique: true,
  },
  Name: {
    allowNull: false,
    type: Sequelize.STRING,
    validate: {
      notEmpty: true,
    },
  },
  Slug: {
    allowNull: false,
    type: Sequelize.STRING,
    validate: {
      notEmpty: true,
    },
  },
  Undiscard: {
    allowNull: false,
    defaultValue: false,
    type: Sequelize.BOOLEAN,
  },
});

export default ((
  Sequelize,
  stack,
): $ModelClass<'ChampionshipResultTableRace'> => {
  const ChampionshipResultTableRace = stack.define(
    'ChampionshipResultTableRace',
    fields(Sequelize),
    {
      tableName: 'ChampionshipResultTableRaces',
    },
  );

  ChampionshipResultTableRace.associate = (models: $ModelClasses) => {
    models.ChampionshipResultTableRace.belongsTo(
      models.ChampionshipResultTable,
      {
        as: 'ChampionshipResultTable',
        foreignKey: 'ChampionshipResultTableId',
      },
    );
    models.ChampionshipResultTableRace.belongsTo(
      models.Regatta,
      {
        as: 'Regatta',
        constraints: false,
        foreignKey: 'RegattaId',
      },
    );
    models.ChampionshipResultTableRace.hasMany(
      models.ChampionshipResultTableRaceResult,
      {
        as: 'ChampionshipResultTableRaceResults',
        foreignKey: 'ChampionshipResultTableRaceId',
      },
    );
  };

  ChampionshipResultTableRace.addScope(
    'defaultScope',
    {
      order: [['createdAt', 'ASC']],
    },
  );

  return ChampionshipResultTableRace;
});
